import { z } from 'zod';
import { entityIdSchema, localeSchema, safeTextSchema } from './common';

/**
 * Media asset metadata with provenance. `hosted` assets are versioned rights-cleared files served
 * under /media/ and must carry a checksum; external sources must be HTTPS. Embedding-origin
 * allowlisting for `approved-embed` is enforced when playback lands (Phase 5).
 */

export const MEDIA_SOURCE_TYPES = ['hosted', 'external-link', 'approved-embed'] as const;
export const MEDIA_MIME_TYPES = ['video/mp4', 'video/webm', 'audio/mpeg'] as const;

const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/, 'Expected a lowercase SHA-256 hex digest');

/** Same-origin static path; no traversal, no query string. */
export const hostedPathSchema = z
  .string()
  .max(200)
  .regex(/^\/media\/[a-z0-9][a-z0-9._/-]*$/, 'Expected a /media/... path')
  .refine((p) => !p.includes('..') && !p.includes('//'), 'Path traversal is not allowed');

const httpsUrlSchema = z.url({ protocol: /^https$/ }).max(500);

export const captionTrackSchema = z.strictObject({
  locale: localeSchema,
  kind: z.enum(['captions', 'subtitles']),
  location: hostedPathSchema,
  checksumSha256: sha256Schema,
});
export type CaptionTrack = z.infer<typeof captionTrackSchema>;

export const mediaAssetFieldsSchema = z.strictObject({
  id: entityIdSchema,
  sourceType: z.enum(MEDIA_SOURCE_TYPES),
  location: z.string().min(1).max(500),
  mimeType: z.enum(MEDIA_MIME_TYPES),
  captions: z.array(captionTrackSchema).max(8),
  checksumSha256: sha256Schema.nullable(),
  provenance: safeTextSchema(500),
  attribution: safeTextSchema(300),
  isFictional: z.boolean(),
  /** Same-origin poster image shown before playback (migration 0005). */
  posterLocation: hostedPathSchema.nullable().default(null),
});

/** Cross-field rules shared by the stored asset and editorial input. */
export function checkMediaAsset(
  m: Pick<MediaAsset, 'sourceType' | 'location' | 'checksumSha256' | 'captions'>,
  ctx: z.RefinementCtx,
): void {
  if (m.sourceType === 'hosted') {
    if (!hostedPathSchema.safeParse(m.location).success) {
      ctx.addIssue({
        code: 'custom',
        path: ['location'],
        message: 'Hosted media needs a /media/ path',
      });
    }
    if (m.checksumSha256 === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['checksumSha256'],
        message: 'Hosted media needs a checksum',
      });
    }
  } else if (!httpsUrlSchema.safeParse(m.location).success) {
    ctx.addIssue({
      code: 'custom',
      path: ['location'],
      message: 'External media must use HTTPS',
    });
  }
  const locales = m.captions.map((c) => c.locale);
  if (new Set(locales).size !== locales.length) {
    ctx.addIssue({ code: 'custom', path: ['captions'], message: 'One caption track per locale' });
  }
}

export const mediaAssetSchema = mediaAssetFieldsSchema.superRefine(checkMediaAsset);
export type MediaAsset = z.output<typeof mediaAssetFieldsSchema>;
